import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { FileText, Phone, Check, ArrowRight, Shield, Clock, MapPin } from 'lucide-react';

export default function EtudeGeotechnique() {
  const { i18n } = useTranslation();
  const isFr = i18n.language === 'fr';
  const { API } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    nom: '', entreprise: '', telephone: '', email: '', type_mission: '', type_projet: '',
    lieu_projet: '', delai_souhaite: '', surface: '', budget: '', rgpd_accepted: false,
  });

  const typesMissions = [
    { value: 'G1', label: 'G1 - Étude géotechnique préalable' },
    { value: 'G2', label: 'G2 - Étude géotechnique de conception' },
    { value: 'G3', label: 'G3 - Étude et suivi géotechniques d\'exécution' },
    { value: 'G4', label: 'G4 - Supervision géotechnique d\'exécution' },
    { value: 'G5', label: 'G5 - Diagnostic géotechnique' },
    { value: 'autre', label: isFr ? 'Autre / Je ne sais pas' : 'Other / I don\'t know' },
  ];

  const typesProjets = [
    { value: 'maison', label: isFr ? 'Maison individuelle' : 'Single house' },
    { value: 'immeuble', label: isFr ? 'Immeuble collectif' : 'Multi-family building' },
    { value: 'infrastructure', label: isFr ? 'Infrastructure / Ouvrage d\'art' : 'Infrastructure' },
    { value: 'industriel', label: isFr ? 'Bâtiment industriel' : 'Industrial building' },
    { value: 'terrassement', label: isFr ? 'Terrassement / VRD' : 'Earthworks' },
    { value: 'autre', label: isFr ? 'Autre' : 'Other' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.rgpd_accepted) { toast.error(isFr ? 'Veuillez accepter les conditions' : 'Please accept'); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.keys(formData).forEach(k => { if (formData[k] !== '' && formData[k] !== null) fd.append(k, formData[k]); });
      await axios.post(`${API}/demandes/etude`, fd);
      setSubmitted(true);
    } catch (error) { toast.error(isFr ? 'Erreur' : 'Error'); }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><Check className="w-10 h-10 text-green-600" /></div>
          <h1 className="text-3xl font-bold text-[#0A2540] mb-4">{isFr ? 'Merci !' : 'Thank you!'}</h1>
          <p className="text-gray-600 mb-6">{isFr ? 'Nous sélectionnons les meilleurs BET pour votre projet. Réponse sous 48h.' : 'We\'re selecting the best firms for your project. Response within 48h.'}</p>
          <div className="flex items-center justify-center gap-2 text-[#635BFF] font-medium"><Phone className="w-5 h-5" /><span>+33 7 82 93 93 83</span></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <section className="pt-16 pb-8 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <div className="inline-flex items-center gap-2 bg-cyan-100 rounded-full px-4 py-2 mb-6">
            <FileText className="w-4 h-4 text-cyan-600" />
            <span className="text-sm font-medium text-cyan-600">{isFr ? 'Étude géotechnique' : 'Geotechnical study'}</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-4">{isFr ? 'Demandez une étude géotechnique' : 'Request a geotechnical study'}</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">{isFr ? 'Missions G1 à G5, diagnostics, expertises... On vous met en relation avec les meilleurs bureaux d\'études.' : 'G1 to G5 missions, diagnostics, expertise... We connect you with the best engineering firms.'}</p>
        </div>
      </section>

      <section className="pb-16 px-4">
        <div className="container mx-auto max-w-2xl">
          <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-6 md:p-8 border border-gray-100">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>{isFr ? 'Nom complet' : 'Full name'} *</Label><Input required value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} className="mt-2" /></div>
                <div><Label>{isFr ? 'Entreprise' : 'Company'}</Label><Input value={formData.entreprise} onChange={(e) => setFormData({ ...formData, entreprise: e.target.value })} className="mt-2" /></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>{isFr ? 'Téléphone' : 'Phone'} *</Label><Input type="tel" required value={formData.telephone} onChange={(e) => setFormData({ ...formData, telephone: e.target.value })} className="mt-2" /></div>
                <div><Label>Email</Label><Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="mt-2" /></div>
              </div>
              <div>
                <Label>{isFr ? 'Type de mission' : 'Mission type'} *</Label>
                <Select value={formData.type_mission} onValueChange={(v) => setFormData({ ...formData, type_mission: v })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder={isFr ? 'Sélectionnez' : 'Select'} /></SelectTrigger>
                  <SelectContent>{typesMissions.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>{isFr ? 'Type de projet' : 'Project type'} *</Label>
                <Select value={formData.type_projet} onValueChange={(v) => setFormData({ ...formData, type_projet: v })}>
                  <SelectTrigger className="mt-2"><SelectValue placeholder={isFr ? 'Sélectionnez' : 'Select'} /></SelectTrigger>
                  <SelectContent>{typesProjets.map((t) => (<SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>{isFr ? 'Lieu du projet' : 'Project location'} *</Label><Input required value={formData.lieu_projet} onChange={(e) => setFormData({ ...formData, lieu_projet: e.target.value })} placeholder="69001 Lyon" className="mt-2" /></div>
              <div><Label>{isFr ? 'Délai souhaité' : 'Desired timeline'} *</Label><Input required value={formData.delai_souhaite} onChange={(e) => setFormData({ ...formData, delai_souhaite: e.target.value })} placeholder={isFr ? 'Ex: 3 semaines' : 'Ex: 3 weeks'} className="mt-2" /></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>{isFr ? 'Surface (m²)' : 'Area (m²)'}</Label><Input value={formData.surface} onChange={(e) => setFormData({ ...formData, surface: e.target.value })} placeholder="500" className="mt-2" /></div>
                <div><Label>{isFr ? 'Budget approximatif' : 'Budget'}</Label><Input value={formData.budget} onChange={(e) => setFormData({ ...formData, budget: e.target.value })} placeholder="5000€" className="mt-2" /></div>
              </div>
              <div className="pt-4 border-t">
                <div className="flex items-start gap-3">
                  <Checkbox id="rgpd" checked={formData.rgpd_accepted} onCheckedChange={(c) => setFormData({ ...formData, rgpd_accepted: c })} />
                  <Label htmlFor="rgpd" className="text-sm text-gray-600 cursor-pointer">{isFr ? "J'accepte le traitement de mes données (RGPD)" : "I accept data processing (GDPR)"}</Label>
                </div>
              </div>
              <Button type="submit" disabled={loading || !formData.rgpd_accepted} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white py-4 text-lg">
                {loading ? <div className="flex items-center gap-2"><div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div></div> : <span className="flex items-center gap-2">{isFr ? 'Demander une étude' : 'Request a study'}<ArrowRight className="w-5 h-5" /></span>}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
